import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SyncStatusSummary } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { BarList } from '@/components/charts/BarList';
import type { BarListItem } from '@/components/charts/BarList';
import { TrendChart } from '@/components/charts/TrendChart';
import type { TrendPoint } from '@/components/charts/TrendChart';
import { IconCloudOff, IconInbox } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { NewCustomersTodayCard, ReturningCustomersTodayCard } from '@/features/portal/CustomersToday';
import { ReportColumnsButton } from '@/components/ReportToolbar';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchDashboard, fetchSalesReport, fetchSyncStatus, portalKeys } from '@/features/portal/api';
import { addDays } from '@/features/portal/filters';
import { formatCount, formatMoney, formatPosBucket, formatPosBucketShort, formatRelative, parseAmount } from '@/lib/format';

/** Shown instead of a wall of zeros before the first POS push lands. */
function NoDataYet({ status }: { status: SyncStatusSummary }) {
  return (
    <div className="card">
      <EmptyState
        icon={<IconCloudOff className="h-5 w-5" />}
        title="No data synced yet"
        description="The POS pushes its data to the cloud roughly every 15 minutes, plus whenever a shift closes. Reports appear here as soon as the first push lands."
      />
      <dl className="grid grid-cols-1 gap-px border-t border-slate-200 bg-slate-200 sm:grid-cols-2">
        <div className="bg-white px-5 py-4">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Registered tills</dt>
          <dd className="mt-1 text-sm font-semibold tabular-nums text-slate-900">
            {formatCount(status.deviceCount)}
          </dd>
        </div>
        <div className="bg-white px-5 py-4">
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Last contact</dt>
          <dd className="mt-1 text-sm font-semibold text-slate-900">
            {status.lastSyncAt === null ? 'Never' : formatRelative(status.lastSyncAt)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** Days the dashboard covers. Enough for the trend to have a shape. */
const WINDOW_DAYS = 30;

/**
 * The blocks the dashboard is made of, in their default order. Each can be
 * hidden or moved from Display options; the choice is saved in this browser
 * only (see `useReportLayout`) — it is a personal view, not cafe data.
 */
const SECTION_KEYS = ['kpis', 'newCustomers', 'returningCustomers', 'dailySales', 'topProducts'] as const;
type DashboardSection = (typeof SECTION_KEYS)[number];

const SECTION_LABELS: Readonly<Record<DashboardSection, string>> = {
  kpis: 'Summary tiles',
  newCustomers: 'New customers today',
  returningCustomers: 'Returning customers today',
  dailySales: 'Daily sales',
  topProducts: 'Top products',
};

/** Grid span of each block on a wide screen; everything is full width on a narrow one. */
const SECTION_SPAN: Readonly<Record<DashboardSection, string>> = {
  kpis: 'xl:col-span-12',
  newCustomers: 'xl:col-span-6',
  returningCustomers: 'xl:col-span-6',
  dailySales: 'xl:col-span-8',
  topProducts: 'xl:col-span-4',
};

export function DashboardPage() {
  const { currency, filters } = usePortal();
  const layout = useReportLayout('dashboard', SECTION_KEYS);

  // A FIXED window of its own, not the shared filter state. The dashboard is an
  // at-a-glance overview and carries no range picker, so reading the shared
  // filters here would peg it to whatever range was last chosen on another page
  // — a period the reader cannot see or change from this screen. It also spans
  // every branch and till deliberately, since neither selector is on this page.
  //
  // `filters.today` is the TENANT's today (resolved in their timezone), not the
  // browser's, so a shop in Dubai does not lose or gain a day when the report is
  // opened from another country.
  const range = useMemo(
    () => ({ from: addDays(filters.today, -(WINDOW_DAYS - 1)), to: filters.today }),
    [filters.today],
  );

  const syncQuery = useQuery({
    queryKey: portalKeys.syncStatus(),
    queryFn: ({ signal }) => fetchSyncStatus(signal),
    staleTime: 60_000,
  });

  const hasData = syncQuery.data?.hasData !== false;

  const kpiQuery = useQuery({
    queryKey: portalKeys.dashboard(range),
    queryFn: ({ signal }) => fetchDashboard(range, signal),
    // A hidden block asks the server for nothing.
    enabled: hasData && layout.isVisible('kpis'),
  });

  const salesQuery = useQuery({
    queryKey: portalKeys.sales(range),
    queryFn: ({ signal }) => fetchSalesReport(range, signal),
    enabled: hasData && (layout.isVisible('dailySales') || layout.isVisible('topProducts')),
  });

  const trendPoints = useMemo<TrendPoint[]>(
    () =>
      (salesQuery.data?.daily ?? []).map((point) => ({
        key: point.bucket,
        label: formatPosBucketShort(point.bucket),
        fullLabel: formatPosBucket(point.bucket),
        value: parseAmount(point.sales),
        display: formatMoney(point.sales, currency),
        meta: `${formatCount(point.orders)} order${point.orders === 1 ? '' : 's'}`,
      })),
    [salesQuery.data, currency],
  );

  const topProducts = useMemo<BarListItem[]>(
    () =>
      (salesQuery.data?.topProducts ?? []).slice(0, 8).map((product) => ({
        key: product.name,
        label: product.name,
        display: formatMoney(product.amount, currency),
        value: parseAmount(product.amount),
        meta: `${formatCount(product.quantity)} sold · ${formatCount(product.orders)} order${
          product.orders === 1 ? '' : 's'
        }`,
      })),
    [salesQuery.data, currency],
  );

  const header = (
    <PageHeader
      title="Dashboard"
      description={`How the restaurant has performed over the last ${WINDOW_DAYS} days, across every branch and till. Use Sales report to pick a range.`}
      actions={<ReportColumnsButton layout={layout} labels={SECTION_LABELS} />}
    />
  );

  // Both reports failing means the server is unreachable, not a partial outage —
  // say so once. A single failing report still shows the other panel's data.
  if (kpiQuery.isError && salesQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={kpiQuery.error}
            onRetry={() => {
              void kpiQuery.refetch();
              void salesQuery.refetch();
            }}
            retrying={kpiQuery.isFetching || salesQuery.isFetching}
          />
        </div>
      </>
    );
  }

  // Each block of the page, keyed so Display options can hide or reorder it.
  const sections: Record<DashboardSection, ReactNode> = {
    kpis: (
      <AsyncSection
        state={kpiQuery}
        skeleton={
          <KpiGrid>
            {Array.from({ length: 8 }).map((_, index) => (
              <KpiTileSkeleton key={index} />
            ))}
          </KpiGrid>
        }
      >
        {(kpis) => (
          <KpiGrid>
            <KpiTile
              label="Total sales"
              value={formatMoney(kpis.totalSales, kpis.currency || currency)}
              hint={`Gross ${formatMoney(kpis.grossSales, kpis.currency || currency)}`}
            />
            <KpiTile label="Orders" value={formatCount(kpis.orderCount)} />
            <KpiTile
              label="Average order"
              value={formatMoney(kpis.averageOrderValue, kpis.currency || currency)}
            />
            <KpiTile label="Items sold" value={formatCount(kpis.itemsSold)} />
            <KpiTile
              label="Returns"
              value={formatMoney(kpis.returnsAmount, kpis.currency || currency)}
              tone="negative"
            />
            <KpiTile label="Tax collected" value={formatMoney(kpis.taxCollected, kpis.currency || currency)} />
            <KpiTile
              label="Discounts"
              value={formatMoney(kpis.discountAmount, kpis.currency || currency)}
              tone="muted"
            />
            <KpiTile
              label="Unpaid"
              value={formatMoney(kpis.unpaidAmount, kpis.currency || currency)}
              hint={`${formatCount(kpis.unpaidCount)} order${kpis.unpaidCount === 1 ? '' : 's'} outstanding`}
              tone={kpis.unpaidCount > 0 ? 'negative' : 'muted'}
            />
          </KpiGrid>
        )}
      </AsyncSection>
    ),
    newCustomers: <NewCustomersTodayCard date={filters.today} />,
    returningCustomers: <ReturningCustomersTodayCard date={filters.today} />,
    dailySales: (
              <SectionCard
                headingId="dashboard-trend"
                title="Daily sales"
                description={`Net sales per day over the last ${WINDOW_DAYS} days.`}
              >
                <div className="px-2 py-4 sm:px-4">
                  <AsyncSection
                    state={salesQuery}
                    skeleton={<Skeleton className="mx-2 h-60" />}
                    isEmpty={(report) => report.daily.length === 0}
                    emptyTitle="No sales in this range"
                    emptyDescription="Pick a wider date range or a different branch."
                  >
                    {() => <TrendChart points={trendPoints} title="Daily sales" valueHeader="Net sales" />}
                  </AsyncSection>
                </div>
              </SectionCard>
    ),
    topProducts: (
            <SectionCard
              headingId="dashboard-top-products"
              title="Top products"
              description="Best sellers by sales value."
            >
              <div className="px-5 py-4">
                <AsyncSection
                  state={salesQuery}
                  skeleton={
                    <div className="space-y-4">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={index} className="h-8" />
                      ))}
                    </div>
                  }
                  isEmpty={(report) => report.topProducts.length === 0}
                  emptyIcon={<IconInbox className="h-5 w-5" />}
                  emptyTitle="No products sold"
                  emptyDescription="Nothing was sold in this range."
                >
                  {() => <BarList items={topProducts} label="Top products by sales value" />}
                </AsyncSection>
              </div>
            </SectionCard>
    ),
  };
  const visibleSections = layout.orderedKeys.filter((key): key is DashboardSection =>
    layout.isVisible(key),
  );

  return (
    <>
      {header}

      {syncQuery.isPending ? (
        <KpiGrid>
          <KpiTileSkeleton />
          <KpiTileSkeleton />
          <KpiTileSkeleton />
          <KpiTileSkeleton />
        </KpiGrid>
      ) : syncQuery.data?.hasData === false ? (
        <NoDataYet status={syncQuery.data} />
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {visibleSections.map((key) => (
            <div key={key} className={SECTION_SPAN[key]}>
              {sections[key]}
            </div>
          ))}
          {visibleSections.length === 0 ? (
            <div className="card xl:col-span-12">
              <EmptyState
                icon={<IconInbox className="h-5 w-5" />}
                title="Everything is hidden"
                description="Open Display options and tick the blocks you want to see."
              />
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
